import * as z from "zod";
import { userFacingError } from "../authorization";
import { USER_GROUPS_NAME_UNIQUE } from "../dbConstraints";
import { isUniqueConstraintViolation } from "../dbErrors";
import { groupNameSchema } from "../groupFields";
import {
  assertCustomRoleScope,
  assertIdpSyncNotActive,
  insertGrantsDeduped,
  grantEntry,
  validateUserGroupGrants,
} from "../rbac/validation";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export { MAX_USER_GROUP_NAME_LENGTH } from "../groupFields";
export { groupNameSchema as userGroupNameField } from "../groupFields";

export const createUserGroup = mutator({
  schema: z.object({
    id: clientGeneratableIdSchema,
    name: groupNameSchema,
    grants: z.array(grantEntry),
  }),
  resolve: {
    grantUsers: { kind: "user", from: "grants", field: "userId" },
    grantCustomRoles: {
      kind: "role",
      from: "grants",
      field: "customRoleId",
    },
  },
  permissions: [
    {
      permission: "tenant:user_group_create",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args: { id, name, grants, grantCustomRoles } }) => {
    await assertIdpSyncNotActive(tx, ctx.tenantId, "groupLifecycle");
    // When the IdP owns group membership ("jit_users_and_groups" or SCIM, the latter
    // already blocked above), a group can be created but no members may be
    // assigned here; plain "jit" provisions users only, so members are allowed.
    if (grants.length > 0) {
      await assertIdpSyncNotActive(tx, ctx.tenantId, "groupMembership");
    }
    validateUserGroupGrants(grants, "user_group");
    for (const role of grantCustomRoles) {
      assertCustomRoleScope(role, "user_group");
    }

    const now = Date.now();

    try {
      await tx.mutate.userGroups.insert({
        id,
        name,
        createdAt: now,
        updatedAt: now,
        tenantId: ctx.tenantId,
      });
    } catch (err) {
      if (isUniqueConstraintViolation(err, USER_GROUPS_NAME_UNIQUE)) {
        throw userFacingError("A group with this name already exists");
      }
      throw err;
    }

    await insertGrantsDeduped(tx, grants, id, ctx.tenantId);
  },
});
